import React from 'react';
import PropTypes from 'prop-types';
import { MdClose, MdEmail } from 'react-icons/md';

// ── Shared avatar helpers ─────────────────────────────────────────────────────

export const getInitials = (name) => {
  if (!name) return '?';
  const parts = String(name).trim().split(/\s+/);
  return parts.length >= 2
    ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    : String(name).substring(0, 2).toUpperCase();
};

// The person's display name for an option: prefer the serializer's full_name,
// else compose it from first_name + last_name. Returns null when only the code
// exists (so callers can skip the secondary line). `code` is the value shown as
// the primary label (the employee's short name/code) — never repeat it here.
export const resolvePersonName = (data = {}, code = '') => {
  const full = (data.fullName || data.full_name || '').trim();
  if (full && full !== code) return full;

  const composed = [
    data.firstName || data.first_name,
    data.lastName  || data.last_name,
  ]
    .map((s) => (s || '').trim())
    .filter(Boolean)
    .join(' ');

  return composed && composed !== code ? composed : null;
};

const nameHash = (name) =>
  String(name || '')
    .split('')
    .reduce((acc, c) => acc + c.charCodeAt(0), 0);

export const getAvatarColor = (name) => {
  const palette = [
    '#4f46e5', // indigo
    '#0891b2', // cyan
    '#059669', // emerald
    '#d97706', // amber
    '#dc2626', // red
    '#7c3aed', // violet
    '#0284c7', // sky
    '#db2777', // pink
  ];
  return palette[nameHash(name) % palette.length];
};

// Professional two-tone gradient per person (deterministic by name) — gives the
// avatar depth instead of a flat single fill. Paired light→dark tones.
const AVATAR_GRADIENTS = [
  ['#6366f1', '#4338ca'], // indigo
  ['#06b6d4', '#0e7490'], // cyan
  ['#10b981', '#047857'], // emerald
  ['#f59e0b', '#b45309'], // amber
  ['#f43f5e', '#be123c'], // rose
  ['#8b5cf6', '#6d28d9'], // violet
  ['#0ea5e9', '#0369a1'], // sky
  ['#ec4899', '#be185d'], // pink
  ['#14b8a6', '#0f766e'], // teal
  ['#f97316', '#c2410c'], // orange
];

export const getAvatarGradient = (name) => {
  const [from, to] = AVATAR_GRADIENTS[nameHash(name) % AVATAR_GRADIENTS.length];
  return `linear-gradient(135deg, ${from} 0%, ${to} 100%)`;
};

// ── Avatar ────────────────────────────────────────────────────────────────────

export const EmployeeAvatar = ({ name, avatarUrl, size = 32, fontSize = 11 }) => (
  <div
    className="tw-flex tw-items-center tw-justify-center tw-shrink-0 tw-font-semibold tw-text-white tw-select-none"
    style={{
      width: size,
      height: size,
      fontSize,
      borderRadius: '50%',
      background: avatarUrl ? 'transparent' : getAvatarGradient(name),
      boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
    }}
  >
      {avatarUrl ? (
        <img
          src={avatarUrl}
          alt={name}
          className="tw-w-full tw-h-full tw-rounded-xl tw-object-cover"
        />
      ) : (
        getInitials(name)
      )}
    </div>
);

EmployeeAvatar.propTypes = {
  name:      PropTypes.string,
  avatarUrl: PropTypes.string,
  size:      PropTypes.number,
  fontSize:  PropTypes.number,
};

// ── Badge (selected-person card) ──────────────────────────────────────────────
/**
 * Displays a selected employee as a compact card with avatar, name, and optional email.
 * Can be used standalone or inside EmployeeSelectField.
 *
 * Props:
 *   name      – employee name (required)
 *   email     – employee email (optional)
 *   avatarUrl – photo URL (optional, falls back to initials)
 *   onClear   – if provided, a × button is shown to deselect
 *   size      – 'sm' | 'md' (default 'md')
 */
const EmployeeBadge = ({ name, email, avatarUrl, onClear, size = 'md' }) => {
  if (!name) return null;

  const isSm = size === 'sm';

  return (
    <div className="tw-flex tw-items-center tw-gap-2.5 tw-px-3 tw-py-2 tw-rounded tw-bg-slate-50 tw-border tw-border-slate-200 tw-mt-1.5">
      <EmployeeAvatar
        name={name}
        avatarUrl={avatarUrl}
        size={isSm ? 26 : 32}
        fontSize={isSm ? 9 : 11}
      />

      <div className="tw-flex tw-flex-col tw-min-w-0 tw-flex-1">
        <span className={`tw-font-semibold tw-text-slate-800 tw-leading-tight tw-truncate ${isSm ? 'tw-text-xs' : 'tw-text-sm'}`}>
          {name}
        </span>
        {email && (
          <span className="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-text-slate-400 tw-truncate tw-mt-0.5">
            <MdEmail size={11} className="tw-shrink-0" />
            {email}
          </span>
        )}
      </div>

      {onClear && (
        <button
          type="button"
          onClick={onClear}
          className="tw-p-1 tw-rounded-sm tw-text-slate-400 hover:tw-text-red-500 hover:tw-bg-red-50 tw-transition-colors tw-border-0 tw-bg-transparent tw-cursor-pointer tw-shrink-0"
          aria-label="Remove"
        >
          <MdClose size={14} />
        </button>
      )}
    </div>
  );
};

EmployeeBadge.propTypes = {
  name:      PropTypes.string.isRequired,
  email:     PropTypes.string,
  avatarUrl: PropTypes.string,
  onClear:   PropTypes.func,
  size:      PropTypes.oneOf(['sm', 'md']),
};

export default EmployeeBadge;
